/* eslint-disable max-len */
import {
    ReactNode, FunctionComponent, MutableRefObject,
} from 'react'
import {
    GridApiPro, GridCallbackDetails,
    GridColDef, GridColumnVisibilityModel,
    GridDataSource, GridFilterItem, GridFilterModel,
    GridGetRowsError, GridRenderCellParams,
    GridRowSelectionModel, GridSlotProps, GridSlotsComponent,
    GridUpdateRowError, GridRowId,
    GridSortModel, GridColumnDimensions, GridRowParams,
    GridPaginationModel,
} from '@mui/x-data-grid-pro'
import AllCellComponentsSpecialProps from 'app/common/DomainObject/ComponentMapper'
import {
    SkyBaseDataGridToolbarProps,
} from 'app/PoC/DataGridDemoPages/components/SkyBaseDataGrid/components/SkyBaseDataGridToolbar/SkyBaseDataGridToolbar.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'

import {
    FilterType,
} from 'app/enums/common.enums'
import {
    RequestConfig,
} from 'app/types/request.types'

/* new Filters types that will be supported only in new Tables */
export enum SkyNetDataGridFilterType {
    Boolean = 'boolean',
    Toggle = 'toggle',
    TimeLessDateRange = 'timeLessDateRange',
}

export type ExtendedFilterType = FilterType | SkyNetDataGridFilterType

type RenderCellProp =
    { renderCell?: (props: GridRenderCellParams) => JSX.Element } | AllCellComponentsSpecialProps

type CustomFilterProps = {
    filterField?: string,
    filterType?: ExtendedFilterType,
    filterable?: boolean,
    filterParams?: {
        defaultValue?: string | number | boolean | Array<string | number | boolean>,
        operator?: string,
    }
}

/* Type to convert SkyNetTableFieldBase config to MUI GridColDef
    SkyNetTableFieldBase -> MUI GridColDef
        sorting          ->    sortable
        name             ->    field
        componentName    ->    renderCell
*/
export type ColumnConfigMappingType<T = any> = {
    sorting?: boolean,
    labelKey?: string,
    name: keyof T,
    minWidth?: number,
    maxWidth?: number,
    width?: number,
    /* if true - column wouldn't be visible but will be added to FilterMenu */
    hiddenFromColumns?: boolean,
    headerName?: string,
    reduced?: boolean,
} & CustomFilterProps & RenderCellProp

export type ExtendedGridApiPro = GridApiPro & {
    goToPage?: (e: unknown, page: number) => void,
    resetToFirstPage?: () => void,
    updateGridDimensions?: () => void
}

/*  DataSource for DataGrid with Error handling */
export type SkyBaseDataSourceProvider = {
    dataSource: GridDataSource,
    onDataSourceError: (rowsError: (GridGetRowsError | GridUpdateRowError)) => void,
    errorMessage?: string,
    showArchived?: boolean,
    key?: RequestKeys | string
    request?: RequestConfig,
    invalidate?: () => void,
}

/* Our custom filterOperators keys for FilterModer */
export enum FilterOperatorType {
    EqualsIncludeFilter = 'equalsIncludeFilter', // for includeFilters
    // for other filters that will be added to request body , like { archived: true }
    DataFilter = 'dataFilter',
}

export type ExtendedGridColDef = GridColDef & ColumnConfigMappingType

export type ExtendedGridFilterItem = GridFilterItem & {
    defaultValue?: string | number | boolean | Array<string | number | boolean>,
}
export type ExtendedGridFilterModel = GridFilterModel & {
    items: ExtendedGridFilterItem[]
}

export type DetailPanelProps = {
    getDetailPanelHeight?: (params: GridRowParams) => number | 'auto',
    getDetailPanelContent?: (params: GridRowParams) => ReactNode,
    detailPanelExpandedRowIds?: GridRowId[],
    onDetailPanelExpandedRowIdsChange?: (ids: Set<GridRowId>, details: GridCallbackDetails) => void
}

export type SkyBaseDataGridProps = {
    persistentKey: string,
    gridApiRef?: MutableRefObject<GridApiPro>,
    columns: ExtendedGridColDef[],
    customDataSource: SkyBaseDataSourceProvider,
    rowHeight?: number | 'auto',
    height?: string | number,
    onRowClick?: (params: any) => void,
    rowSelectionModel?: GridRowSelectionModel,
    onRowSelectionModelChange?: (newRowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void,
    tableWidthPercent?: number,
    initialTableWidthPercent?: number,
    showToolbar?: boolean,
    slots?: Partial<GridSlotsComponent>,
    slotProps?: GridSlotProps,
    classNames?: {
        container?: string,
        tableWrapper?: string,
    },
    visibilityModel: GridColumnVisibilityModel,
    onColumnVisibilityModelChange: (model: GridColumnVisibilityModel, wasSetByUser?: boolean) => void,
    Pagination?: FunctionComponent,
    paginationModel?: GridPaginationModel,
    onPaginationModelChange?: (model: GridPaginationModel) => void,
    CustomToolbar?: FunctionComponent
    toolbarProps?: SkyBaseDataGridToolbarProps
    CustomFooter?: FunctionComponent,
    customColumnsSetStateConverter?: (value: SkyDataGridColumnsState, oldState?: SkyDataGridLocalStorage) => SkyDataGridLocalStorage,
    customColumnsInitStateConverter?: (dataGridState: SkyDataGridLocalStorage) => SkyDataGridColumnsState,
    enableSortFilterPersistence?: boolean,
    selectionModelType?: 'include' | 'exclude',
    checkboxSelection?: boolean,
    disableMultipleRowSelection?: boolean,
    disabled?: boolean,
    getRowId?: (row: any) => string | number,
} & DetailPanelProps

export type SkyDataGridColumnsState = {
    orderedFields?: string[],
    dimensions?: Record<string, GridColumnDimensions>,
}

/* State that can be persisted separately for specific view */
export type SkyDataGridCustomViewState = {
    columns?: SkyDataGridColumnsState,
    visibilityModel?: GridColumnVisibilityModel,
}

export type SkyDataGridDetailViewState = SkyDataGridCustomViewState & {
    visibilityModelByResize?: GridColumnVisibilityModel,
}

export type SkyDataGridLocalStorage = SkyDataGridCustomViewState & {
    filterModel?: GridFilterModel,
    sortModel?: GridSortModel,
    tableWidthPercent?: number,
    detailsView?: SkyDataGridDetailViewState,
}
